import { Component, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

import {
  CalendarMarker,
  CalendarMarkerTemplate
} from '../../../models/calendar-marker.model';
import {
  CalendarTimezoneData,
  CalendarTimezoneDataService,
  CalendarTimezoneMapRegion,
  filterCalendarTimezones
} from './calendar-timezone-data.service';

const TIMEZONE_RESULT_LIMIT = 60;
const ESCAPE_KEY_CODE = 27;
const ENTER_KEY_CODE = 13;
const ARROW_UP_KEY_CODE = 38;
const ARROW_DOWN_KEY_CODE = 40;
const MARKER_NAME_MAX_LENGTH = 128;
const DEFAULT_MARKER_TEMPLATE: CalendarMarkerTemplate = 'WEEKENDS_ONLY';
const EMPTY_ZONE_ID = '';

export const CalendarTimezoneMarkerModalModes = {
  CREATE: 'create' as 'create',
  EDIT: 'edit' as 'edit',
  ARCHIVE: 'archive' as 'archive'
};

export type CalendarTimezoneMarkerModalMode = typeof CalendarTimezoneMarkerModalModes.CREATE
  | typeof CalendarTimezoneMarkerModalModes.EDIT
  | typeof CalendarTimezoneMarkerModalModes.ARCHIVE;

export interface CalendarTimezoneMarkerCreateResult {
  mode: typeof CalendarTimezoneMarkerModalModes.CREATE;
  name?: string;
  zoneId: string;
  template: CalendarMarkerTemplate;
}

export interface CalendarTimezoneMarkerEditResult {
  mode: typeof CalendarTimezoneMarkerModalModes.EDIT;
  markerId: string;
  name?: string;
  zoneId: string;
}

export interface CalendarTimezoneMarkerArchiveResult {
  mode: typeof CalendarTimezoneMarkerModalModes.ARCHIVE;
  markerId: string;
  replacementMarkerId: string;
}

export type CalendarTimezoneMarkerModalResult = CalendarTimezoneMarkerCreateResult
  | CalendarTimezoneMarkerEditResult
  | CalendarTimezoneMarkerArchiveResult;

@Component({
  selector: 'app-calendar-timezone-marker-modal',
  templateUrl: './calendar-timezone-marker-modal.component.html',
  styleUrls: ['./calendar-timezone-marker-modal.component.css']
})
export class CalendarTimezoneMarkerModalComponent implements OnDestroy {
  public mode: CalendarTimezoneMarkerModalMode = CalendarTimezoneMarkerModalModes.CREATE;
  public marker: CalendarMarker;
  public markerName = '';
  public markerNameMaxLength = MARKER_NAME_MAX_LENGTH;
  public selectedZoneId = '';
  public timezoneQuery = '';
  public availableTimezones: string[] = [];
  public filteredTimezones: string[] = [];
  public mapRegions: CalendarTimezoneMapRegion[] = [];
  public mapViewBox = '0 0 960 420';
  public highlightedOffsetHours: number = null;
  public selectedOffsetHours: number = null;
  public offsetFilterHours: number = null;
  public activeTimezoneIndex = 0;
  public isTimezoneListOpen = false;
  public isTimezoneDataLoading = false;
  public timezoneDataError = false;
  public template: CalendarMarkerTemplate = DEFAULT_MARKER_TEMPLATE;
  public replacementMarkerId = '';
  public replacementMarkers: CalendarMarker[] = [];
  public selectedCurrentOffset = '';
  public selectedCurrentLocalTime = '';

  private destroy$ = new Subject<void>();
  private clockTimer: number;
  private blurTimer: number;

  constructor(
    public activeModal: NgbActiveModal,
    private timezoneDataService: CalendarTimezoneDataService
  ) {}

  public configure(
    mode: CalendarTimezoneMarkerModalMode,
    marker: CalendarMarker,
    markers: CalendarMarker[]
  ): void {
    this.mode = mode;
    this.marker = marker;
    this.markerName = this.resolveSecondaryMarkerName(marker);
    this.selectedZoneId = marker && marker.zoneId ? marker.zoneId : EMPTY_ZONE_ID;
    this.timezoneQuery = this.selectedZoneId;
    this.replacementMarkers = this.buildReplacementMarkers(markers || []);
    this.replacementMarkerId = this.resolveDefaultReplacementMarkerId();

    if (this.isArchiveMode) {
      return;
    }

    this.loadTimezoneData();
    this.updateTimeSnapshot();
    this.startClock();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearTimers();
  }

  public retryTimezoneData(): void {
    this.loadTimezoneData();
  }

  public updateTimezoneQuery(query: string): void {
    this.timezoneQuery = query || '';
    this.offsetFilterHours = null;
    this.isTimezoneListOpen = true;
    this.refreshFilteredTimezones();
  }

  public openTimezoneList(): void {
    this.isTimezoneListOpen = true;
    this.refreshFilteredTimezones();
  }

  public closeTimezoneListAfterBlur(): void {
    this.clearBlurTimer();
    this.blurTimer = window.setTimeout(() => {
      this.isTimezoneListOpen = false;
    }, 120);
  }

  public keepTimezoneListOpen(event: Event): void {
    event.preventDefault();
    this.clearBlurTimer();
  }

  public handleTimezoneInputKeydown(event: KeyboardEvent): void {
    if (event.keyCode === ARROW_DOWN_KEY_CODE) {
      this.moveActiveTimezone(1);
      event.preventDefault();
      return;
    }
    if (event.keyCode === ARROW_UP_KEY_CODE) {
      this.moveActiveTimezone(-1);
      event.preventDefault();
      return;
    }
    if (event.keyCode === ENTER_KEY_CODE && this.isTimezoneListOpen) {
      this.selectActiveTimezone();
      event.preventDefault();
      return;
    }
    if (event.keyCode === ESCAPE_KEY_CODE && this.isTimezoneListOpen) {
      this.isTimezoneListOpen = false;
      event.stopPropagation();
      event.preventDefault();
    }
  }

  public selectTimezone(zoneId: string): void {
    if (!zoneId || this.availableTimezones.indexOf(zoneId) === -1) {
      return;
    }
    this.selectedZoneId = zoneId;
    this.timezoneQuery = zoneId;
    this.offsetFilterHours = null;
    this.isTimezoneListOpen = false;
    this.updateTimeSnapshot();
  }

  public selectMapTimezone(region: CalendarTimezoneMapRegion, event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    if (!region) {
      return;
    }
    this.offsetFilterHours = region.offsetHours;
    this.highlightedOffsetHours = region.offsetHours;
    this.timezoneQuery = '';
    this.isTimezoneListOpen = true;
    this.refreshFilteredTimezones();
  }

  public highlightTimezone(zoneId: string): void {
    var offsetMinutes = resolveTimezoneOffsetMinutes(zoneId, new Date());
    this.highlightedOffsetHours = offsetMinutes === null ? this.selectedOffsetHours : normalizeOffsetHours(offsetMinutes);
  }

  public restoreSelectedTimezoneHighlight(): void {
    this.highlightedOffsetHours = this.selectedOffsetHours;
  }

  public isTimezoneHighlighted(region: CalendarTimezoneMapRegion): boolean {
    if (!region) {
      return false;
    }
    return region.offsetHours === this.highlightedOffsetHours;
  }

  public isMapRegionSelected(region: CalendarTimezoneMapRegion): boolean {
    return !!region && region.offsetHours === this.selectedOffsetHours;
  }

  public getHighlightedOffsetLabel(): string {
    return this.highlightedOffsetHours === null ? '' : formatUtcOffset(this.highlightedOffsetHours * 60);
  }

  public isTimezoneSelected(zoneId: string): boolean {
    return zoneId === this.selectedZoneId;
  }

  public isTimezoneOptionActive(index: number): boolean {
    return this.isTimezoneListOpen && index === this.activeTimezoneIndex;
  }

  public getActiveTimezoneOptionId(): string {
    if (!this.isTimezoneListOpen || !this.filteredTimezones.length) {
      return '';
    }
    return 'calendar-timezone-option-' + this.activeTimezoneIndex;
  }

  public getMarkerDisplayName(marker: CalendarMarker): string {
    if (!marker) {
      return '';
    }
    return marker.displayName || marker.zoneId || marker.name || marker.id;
  }

  public trackByTimezone(index: number, zoneId: string): string {
    return zoneId;
  }

  public trackByMapRegion(index: number, region: CalendarTimezoneMapRegion): string {
    return region.key;
  }

  public trackByMarker(index: number, marker: CalendarMarker): string {
    return marker.id;
  }

  public cancel(): void {
    this.activeModal.dismiss();
  }

  public save(): void {
    if (!this.canSave) {
      return;
    }

    if (this.isArchiveMode) {
      this.activeModal.close(this.buildArchiveResult());
      return;
    }

    this.activeModal.close(this.buildTimezoneResult());
  }

  public get isCreateMode(): boolean {
    return this.mode === CalendarTimezoneMarkerModalModes.CREATE;
  }

  public get isEditMode(): boolean {
    return this.mode === CalendarTimezoneMarkerModalModes.EDIT;
  }

  public get isArchiveMode(): boolean {
    return this.mode === CalendarTimezoneMarkerModalModes.ARCHIVE;
  }

  public get requiresReplacement(): boolean {
    return this.isArchiveMode;
  }

  public get canSave(): boolean {
    if (this.isArchiveMode) {
      return !this.requiresReplacement || this.replacementMarkerId.length > 0;
    }
    return !this.isTimezoneDataLoading && !this.timezoneDataError &&
      this.selectedZoneId.length > 0 && this.normalizedMarkerName.length <= MARKER_NAME_MAX_LENGTH;
  }

  public get normalizedMarkerName(): string {
    return this.markerName ? this.markerName.trim() : '';
  }

  private loadTimezoneData(): void {
    this.isTimezoneDataLoading = true;
    this.timezoneDataError = false;
    this.timezoneDataService.load().takeUntil(this.destroy$).subscribe(
      (data: CalendarTimezoneData) => this.handleTimezoneDataLoaded(data),
      () => this.handleTimezoneDataError()
    );
  }

  private handleTimezoneDataLoaded(data: CalendarTimezoneData): void {
    this.availableTimezones = data.zones || [];
    this.mapRegions = data.mapRegions || [];
    this.mapViewBox = data.mapViewBox || this.mapViewBox;
    this.isTimezoneDataLoading = false;
    this.timezoneDataError = false;
    this.ensureSelectedTimezoneIsAvailable();
    this.refreshFilteredTimezones();
  }

  private handleTimezoneDataError(): void {
    this.isTimezoneDataLoading = false;
    this.timezoneDataError = true;
    this.availableTimezones = [];
    this.filteredTimezones = [];
    this.mapRegions = [];
  }

  private ensureSelectedTimezoneIsAvailable(): void {
    if (!this.selectedZoneId || this.availableTimezones.indexOf(this.selectedZoneId) !== -1) {
      return;
    }
    this.availableTimezones.push(this.selectedZoneId);
    this.availableTimezones.sort((first: string, second: string) => first.localeCompare(second));
  }

  private refreshFilteredTimezones(): void {
    var zones = this.availableTimezones;
    if (this.offsetFilterHours !== null) {
      zones = zones.filter((zoneId: string) => {
        var offsetMinutes = resolveTimezoneOffsetMinutes(zoneId, new Date());
        return offsetMinutes !== null && normalizeOffsetHours(offsetMinutes) === this.offsetFilterHours;
      });
    }
    this.filteredTimezones = filterCalendarTimezones(
      zones,
      this.timezoneQuery,
      TIMEZONE_RESULT_LIMIT
    );
    var selectedIndex = this.filteredTimezones.indexOf(this.selectedZoneId);
    this.activeTimezoneIndex = selectedIndex >= 0 ? selectedIndex : 0;
    this.highlightActiveTimezone();
  }

  private moveActiveTimezone(step: number): void {
    if (!this.isTimezoneListOpen) {
      this.openTimezoneList();
    }
    if (!this.filteredTimezones.length) {
      return;
    }
    this.activeTimezoneIndex += step;
    if (this.activeTimezoneIndex < 0) {
      this.activeTimezoneIndex = this.filteredTimezones.length - 1;
    }
    if (this.activeTimezoneIndex >= this.filteredTimezones.length) {
      this.activeTimezoneIndex = 0;
    }
    this.highlightActiveTimezone();
  }

  private highlightActiveTimezone(): void {
    if (!this.filteredTimezones.length) {
      this.restoreSelectedTimezoneHighlight();
      return;
    }
    this.highlightTimezone(this.filteredTimezones[this.activeTimezoneIndex]);
  }

  private selectActiveTimezone(): void {
    if (!this.filteredTimezones.length) {
      return;
    }
    this.selectTimezone(this.filteredTimezones[this.activeTimezoneIndex]);
  }

  private buildTimezoneResult(): CalendarTimezoneMarkerModalResult {
    if (this.isCreateMode) {
      return {
        mode: CalendarTimezoneMarkerModalModes.CREATE,
        name: this.normalizedMarkerName || undefined,
        zoneId: this.selectedZoneId,
        template: this.template
      };
    }

    return {
      mode: CalendarTimezoneMarkerModalModes.EDIT,
      markerId: this.marker.id,
      name: this.normalizedMarkerName || undefined,
      zoneId: this.selectedZoneId
    };
  }

  private buildArchiveResult(): CalendarTimezoneMarkerModalResult {
    return {
      mode: CalendarTimezoneMarkerModalModes.ARCHIVE,
      markerId: this.marker.id,
      replacementMarkerId: this.replacementMarkerId
    };
  }

  private buildReplacementMarkers(markers: CalendarMarker[]): CalendarMarker[] {
    return markers.filter((candidate: CalendarMarker) => {
      return !!candidate && candidate.active !== false && (!this.marker || candidate.id !== this.marker.id);
    }).sort((first: CalendarMarker, second: CalendarMarker) => {
      if (first.stock) {
        return -1;
      }
      if (second.stock) {
        return 1;
      }
      return this.getMarkerDisplayName(first).localeCompare(this.getMarkerDisplayName(second));
    });
  }

  private resolveSecondaryMarkerName(marker: CalendarMarker): string {
    if (!marker || !marker.name || marker.name === marker.zoneId) {
      return '';
    }
    return marker.name;
  }

  private resolveDefaultReplacementMarkerId(): string {
    if (!this.replacementMarkers.length) {
      return '';
    }
    return this.replacementMarkers[0].id;
  }

  private updateTimeSnapshot(): void {
    if (!this.selectedZoneId) {
      this.selectedCurrentOffset = '';
      this.selectedCurrentLocalTime = '';
      return;
    }

    try {
      var now = new Date();
      var localizedText = now.toLocaleString('en-US', {
        timeZone: this.selectedZoneId,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      var offsetMinutes = resolveTimezoneOffsetMinutes(this.selectedZoneId, now);
      if (offsetMinutes === null) {
        this.useMarkerTimeSnapshot();
        return;
      }
      this.selectedCurrentLocalTime = localizedText;
      this.selectedCurrentOffset = formatUtcOffset(offsetMinutes);
      this.selectedOffsetHours = normalizeOffsetHours(offsetMinutes);
      this.restoreSelectedTimezoneHighlight();
    } catch (error) {
      this.useMarkerTimeSnapshot();
    }
  }

  private useMarkerTimeSnapshot(): void {
    var markerHasSelectedZone = this.marker && this.marker.zoneId === this.selectedZoneId;
    this.selectedCurrentOffset = markerHasSelectedZone && this.marker.currentOffset ? this.marker.currentOffset : '';
    this.selectedCurrentLocalTime = markerHasSelectedZone && this.marker.currentLocalTime ? this.marker.currentLocalTime : '';
    var offsetMinutes = parseUtcOffset(this.selectedCurrentOffset);
    this.selectedOffsetHours = offsetMinutes === null ? null : normalizeOffsetHours(offsetMinutes);
    this.restoreSelectedTimezoneHighlight();
  }

  private startClock(): void {
    this.clearClockTimer();
    this.clockTimer = window.setInterval(() => this.updateTimeSnapshot(), 60000);
  }

  private clearTimers(): void {
    this.clearClockTimer();
    this.clearBlurTimer();
  }

  private clearClockTimer(): void {
    if (this.clockTimer) {
      window.clearInterval(this.clockTimer);
      this.clockTimer = null;
    }
  }

  private clearBlurTimer(): void {
    if (this.blurTimer) {
      window.clearTimeout(this.blurTimer);
      this.blurTimer = null;
    }
  }
}

function formatUtcOffset(offsetMinutes: number): string {
  var sign = offsetMinutes < 0 ? '-' : '+';
  var absoluteMinutes = Math.abs(offsetMinutes);
  var hours = Math.floor(absoluteMinutes / 60);
  var minutes = absoluteMinutes % 60;
  return 'UTC' + sign + padNumber(hours) + ':' + padNumber(minutes);
}

function resolveTimezoneOffsetMinutes(zoneId: string, now: Date): number {
  if (!zoneId) {
    return null;
  }
  try {
    var localizedDate = new Date(now.toLocaleString('en-US', { timeZone: zoneId }));
    var utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    return Math.round((localizedDate.getTime() - utcDate.getTime()) / 60000);
  } catch (error) {
    return null;
  }
}

function normalizeOffsetHours(offsetMinutes: number): number {
  var roundedHours = offsetMinutes < 0 ? -Math.round(Math.abs(offsetMinutes) / 60) : Math.round(offsetMinutes / 60);
  return Math.max(-11, Math.min(12, roundedHours));
}

function parseUtcOffset(value: string): number {
  var match = /^UTC([+-])(\d{2}):(\d{2})$/.exec(value || '');
  if (!match) {
    return null;
  }
  var minutes = Number(match[2]) * 60 + Number(match[3]);
  return match[1] === '-' ? -minutes : minutes;
}

function padNumber(value: number): string {
  return value < 10 ? '0' + value : String(value);
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/task-fullview-popup/calendar-timezone-marker-modal.component.ts